// Office Truck Dispatch board — create trucking job cards per day, assign a
// truck, drag & drop drivers onto cards. Fully separate from the normal
// job dispatch; pulls trucks from Fleet and drivers flagged truck_driver.
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { truckingOverview } from "@/lib/truckingOverview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { nzTodayIso, shiftIsoDay } from "@/lib/dates";
import {
  Truck, Plus, ChevronLeft, ChevronRight, ArrowRight, Trash2, Pencil,
  GripVertical, X, AlertTriangle, Clock, Users, Camera, ChevronDown, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { BillCartageDialog } from "@/components/BillCartageDialog";
import TruckingMaterialLibrary from "@/components/TruckingMaterialLibrary";
import JobEditorDialog from "@/components/TruckingJobEditor";
import { useAuth } from "@/lib/auth";
import { PhotoLightbox } from "@/components/PhotoLightbox";

import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const TYPE_LABELS = {
  cart_aggregate: "Deliver material",
  tip_clay: "Remove material",
  machinery_pickup: "Move plant",
  general_cartage: "General cartage",
  other: "Move material",
};

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-100" },
  in_progress: { label: "In progress", cls: "bg-[#FF5F15] text-white" },
  done: { label: "Done", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-400 text-white line-through" },
};

function validDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const parsed = new Date(value + "T12:00:00");
  return !Number.isNaN(parsed.getTime()) && shiftIsoDay(value, 0) === value;
}

export default function TruckDispatch() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the single selected-day source, including direct links and Back/Forward.
  const date = validDay(searchParams.get("date")) ? searchParams.get("date") : nzTodayIso();
  const selectedDate = useRef(date);
  selectedDate.current = date;
  const setDate = (nextDate) => {
    if (!validDay(nextDate) || nextDate === date) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set("date", nextDate);
      return next;
    });
  };
  const [loadedDate, setLoadedDate] = useState(null);
  const [board, setBoard] = useState({ jobs: [], drivers: [], trucks: [] });
  const [fetching, setLoading] = useState(true);
  const [boardError, setBoardError] = useState(null);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const requestId = useRef(0);
  const [editorJob, setEditorJob] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [billOpen, setBillOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [flJobs, setFlJobs] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialError, setMaterialError] = useState(null);
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditorJob(null);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  const loadMaterials = () => {
    api.get("/trucking/materials").then((r) => { setMaterials(r.data.items || []); setMaterialError(null); }).catch(() => setMaterialError("Could not load materials."));
  };
  useEffect(loadMaterials, []);

  const load = () => {
    const requestedDate = selectedDate.current;
    const request = ++requestId.current;
    setLoading(true); setBoardError(null);
    api.get("/trucking/board", { params: { date: requestedDate } })
      .then((r) => { if (request === requestId.current && requestedDate === selectedDate.current) { setBoard(r.data); setLoadedDate(requestedDate); } })
      .catch((e) => { if (request === requestId.current && requestedDate === selectedDate.current) { setBoardError(e?.response?.data?.detail || "Failed to load board"); setLoadedDate(requestedDate); } })
      .finally(() => { if (request === requestId.current) setLoading(false); });
  };
  useEffect(() => { load(); return () => { requestId.current += 1; }; }, [date]);
  const loading = fetching || loadedDate !== date;
  useEffect(() => {
    if (!loading && location.hash.startsWith("#trucking-card-")) {
      document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView({ block: "center" });
    }
  }, [loading, board.jobs, location.hash]);


  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/jobs").then((r) => {
      const d = r.data;
      setFlJobs(Array.isArray(d) ? d : d?.items || []);
    }).catch(() => {});
  }, []);

  const patchJob = async (id, patch) => {
    try {
      const { data } = await api.patch(`/trucking/jobs/${id}`, patch);
      setBoard((b) => ({ ...b, jobs: b.jobs.map((j) => (j.id === id ? data : j)) }));
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const assignDriver = async (job, driverId) => {
    const ids = job.driver_ids || [];
    if (ids.includes(driverId)) return;
    await patchJob(job.id, { driver_ids: [...ids, driverId] });
    load(); // refresh driver counts
  };

  const unassignDriver = async (job, driverId) => {
    await patchJob(job.id, { driver_ids: (job.driver_ids || []).filter((d) => d !== driverId) });
    load();
  };

  const removeJob = async (job) => {
    if (!window.confirm("Delete this trucking job card?")) return;
    try {
      await api.delete(`/trucking/jobs/${job.id}`);
      setBoard((b) => ({ ...b, jobs: b.jobs.filter((j) => j.id !== job.id) }));
    } catch {
      toast.error("Delete failed");
    }
  };

  const onDrop = (e, job) => {
    e.preventDefault();
    const driverId = e.dataTransfer.getData("text/driver-id");
    if (driverId) assignDriver(job, driverId);
  };

  const dayLabel = new Date(date + "T12:00:00").toLocaleDateString("en-NZ", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const summary = truckingOverview(board.jobs);
  const countedRuns = board.jobs.filter(j => j.status !== "cancelled");
  const assignedDrivers = new Set(countedRuns.flatMap(j => j.driver_ids || [])).size;
  const driverRuns = id => board.jobs.filter(j => (j.driver_ids || []).includes(id)).length;
  const truckPool = new Map(board.trucks.filter(t => /truck|tipper|hiab|prime.?mover|tractor.?unit|low.?loader/i.test(t.vehicle_type || "")).map(t => [t.id, t]));
  // Include every assigned asset, even if Fleet classifies it differently or it is inactive.
  board.jobs.forEach(j => { if (j.truck_id && !truckPool.has(j.truck_id)) truckPool.set(j.truck_id, { id: j.truck_id, rego: j.truck?.rego, label: j.truck?.label }); });
  const trucks = [...truckPool.values()];
  const runOrder = job => job.status === "cancelled" ? 4 : job.status === "done" ? 3 : !job.truck_id || !(job.driver_ids || []).length ? 0 : job.status === "pending" ? 1 : 2;
  const orderedRuns = [...board.jobs].sort((a, b) => runOrder(a) - runOrder(b));

  return (
    <div className="p-4 sm:p-6 lg:p-8 mx-auto pb-24 lg:pb-10" data-testid="truck-dispatch-page">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><p className="label-tag mb-1">// Truck dispatch</p><h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">Truck Dispatch</h1><p className="text-sm text-zinc-500 mt-1">Plan today's runs, assign trucks and drivers, and track actual cartage.</p></div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/trucking/ready-to-bill" className="rounded-md px-3 py-2 font-semibold text-xs text-zinc-600 dark:text-zinc-300 hover:underline">Ready to Bill</Link>
          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Dispatch actions"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onSelect={() => setBillOpen(true)} data-testid="trucking-bill-cartage-btn">Legacy Bill Cartage</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
        </div>
      </header>

      {/* Day picker */}
      <div className="flex items-center gap-2 mb-3 flex-wrap rounded-md border border-zinc-200 dark:border-white/10 p-2 bg-white dark:bg-[#151C27]">
        <Button variant="outline" size="icon" className="h-9 w-9 rounded-md shrink-0" onClick={() => setDate(shiftIsoDay(date, -1))} aria-label="Previous day" data-testid="trucking-prev-day">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="font-display font-black uppercase tracking-tight text-sm sm:text-base min-w-0 flex-1 sm:flex-none sm:min-w-[190px] text-center" data-testid="trucking-day-label">{dayLabel}</div>
        <Button variant="outline" size="icon" className="h-9 w-9 rounded-md shrink-0" onClick={() => setDate(shiftIsoDay(date, 1))} aria-label="Next day" data-testid="trucking-next-day">
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-40 h-9 rounded-sm border-zinc-400" aria-label="Dispatch date" data-testid="trucking-date-input" />
        <Button variant="outline" size="sm" className="h-9 rounded-md text-xs" onClick={() => setDate(nzTodayIso())}>Today</Button>
        <Button onClick={() => setEditorJob(null)} className="sm:ml-auto rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold text-xs" data-testid="trucking-new-job-btn"><Plus className="w-4 h-4 mr-1" /> New Run</Button>
      </div>

      {!loading && !boardError && <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4 px-1 py-2" data-testid="trucking-daily-summary">
        {[["Runs", summary.jobs], ["Active", summary.inProgress], ["Drivers assigned", assignedDrivers], ["Trucks assigned", summary.assignedTrucks], ["Loads", summary.loads], ["t recorded", summary.tonnes]].map(([label, value]) => <div key={label} className="flex items-baseline gap-1.5 text-xs"><span className="font-bold text-base">{value}</span><span className="text-zinc-500">{label}</span></div>)}
      </div>}
      {!loading && !boardError && summary.cancelled > 0 && <p className="text-[11px] text-zinc-500 mb-3">{summary.cancelled} cancelled run{summary.cancelled === 1 ? "" : "s"} shown below; excluded from daily totals.</p>}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(200px,24%)] gap-4 items-start">
        <div className="min-w-0 lg:order-2 lg:sticky lg:top-4">
          <button type="button" onClick={() => setResourcesOpen(v => !v)} aria-expanded={resourcesOpen} aria-controls="dispatch-resources" className="lg:hidden w-full flex justify-between items-center border rounded-md p-3 text-xs font-bold uppercase mb-2">Resources<ChevronDown className={`w-4 h-4 ${resourcesOpen ? "rotate-180" : ""}`} /></button>
          <h2 className="hidden lg:block text-xs font-bold uppercase tracking-wide mb-3">Resources</h2>
          <div id="dispatch-resources" className={`${resourcesOpen ? "block" : "hidden"} lg:block space-y-3`}>
          {loading || boardError ? <p className="text-xs text-zinc-500 p-3">{loading ? "Loading resources…" : "Resources unavailable"}</p> : <>
        {/* Drivers pool */}
        <aside className="hard-card p-4" data-testid="trucking-drivers-pool">
          <div className="label-tag mb-3 flex items-center gap-1.5"><Users className="w-3 h-3" /> Drivers <span className="ml-auto">{board.drivers.length}</span></div>
          {board.drivers.length === 0 ? (
            <div><p className="text-xs font-bold uppercase">No truck drivers listed</p><p className="text-xs text-zinc-500 mt-2">Workers marked as Truck Driver in Team appear here for dispatch.</p>{["admin", "office", "super_admin"].includes(user?.role) && <Link to="/app/team" className="inline-block text-xs font-bold text-[#FF5F15] mt-3">Open Team →</Link>}</div>
          ) : (
            <div className="space-y-1.5">
              {board.drivers.map((d) => (
                <div
                  key={d.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/driver-id", d.id)}
                  className="flex items-center gap-2 border border-zinc-300 dark:border-white/15 bg-white dark:bg-[#151C27] rounded-md px-2.5 py-2 cursor-grab active:cursor-grabbing hover:border-[#FF5F15] transition-colors select-none"
                  data-testid={`trucking-driver-chip-${d.id}`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                  <span className="text-sm font-bold flex-1 min-w-0 truncate">{d.name}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 ${driverRuns(d.id) ? "bg-[#09090B] text-[#FF5F15]" : "bg-zinc-100 text-zinc-500"}`}>
                    {driverRuns(d.id)} {driverRuns(d.id) === 1 ? "run" : "runs"}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-zinc-400 mt-2">Drag a driver onto a run or use “+ Driver”.</p>
            </div>
          )}
        </aside>

        <aside className="hard-card p-4" data-testid="trucking-trucks-pool">
          <div className="label-tag flex items-center gap-1.5 mb-2"><Truck className="w-3 h-3" /> Trucks <span className="ml-auto">{trucks.length}</span></div>
          <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100 dark:divide-white/10">{trucks.map(t => {
            const count = board.jobs.filter(j => j.truck_id === t.id).length;
            return <div key={t.id} className="py-2 flex justify-between items-center gap-2 text-xs"><span className="font-bold truncate" title={t.label || `${t.make || ""} ${t.model || ""}`}>{t.rego || "Assigned vehicle"}</span><span className="text-zinc-500 shrink-0">{count ? `${count} ${count === 1 ? "run" : "runs"}` : "Unassigned"}</span></div>;
          })}</div>
          {!trucks.length && <p className="text-xs text-zinc-500">No trucks listed for this board.</p>}
          <p className="text-[10px] text-zinc-500 mt-2">Selected-day assignments. Change trucks with Edit run.</p>
        </aside>
        </>}
        <div className="border-t border-zinc-200 dark:border-white/10 pt-3"><p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-2">Material library</p><TruckingMaterialLibrary materials={materials} error={materialError} onChanged={loadMaterials} /></div>
          </div>
        </div>

        {/* Job cards */}
        <div className="space-y-3 min-w-0 lg:order-1">
          <div><h2 className="text-xs font-bold uppercase tracking-wide">{date === nzTodayIso() ? "Today’s runs" : "Scheduled runs"} <span className="text-zinc-500 font-normal">· {dayLabel}</span></h2></div>
          {boardError && !loading ? <div role="alert" className="hard-card p-4 text-sm">{boardError}<Button variant="outline" onClick={load} className="ml-3">Retry</Button></div> : loading ? (
            <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-36 bg-zinc-100 animate-pulse border border-zinc-200" />)}</div>
          ) : board.jobs.length === 0 ? (
            <div className="hard-card p-5" data-testid="trucking-empty">
              <h2 className="text-sm font-extrabold uppercase">No runs scheduled</h2><p className="text-sm text-zinc-500 mt-2">No trucking runs are scheduled for {dayLabel}.</p>
              <Button onClick={() => setEditorJob(null)} className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider" size="sm">
                <Plus className="w-4 h-4 mr-1" /> New run
              </Button>
            </div>
          ) : (
            orderedRuns.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                drivers={board.drivers}
                onDrop={(e) => onDrop(e, job)}
                onAssign={(d) => assignDriver(job, d)}
                onUnassign={(d) => unassignDriver(job, d)}
                onEdit={() => setEditorJob(job)}
                onDelete={() => removeJob(job)}
                onStatus={(s) => patchJob(job.id, { status: s })}
              />
            ))
          )}
        </div>
      </div>

      {editorJob !== undefined && (
        <JobEditorDialog
          initial={editorJob}
          date={date}
          trucks={board.trucks}
          drivers={board.drivers}
          clients={clients}
          flJobs={flJobs}
          materials={materials}
          onClose={() => setEditorJob(undefined)}
          onSaved={(savedDate) => { setEditorJob(undefined); if (savedDate !== date) setDate(savedDate); else load(); }}
        />
      )}
      {billOpen && (
        <BillCartageDialog
          date={date}
          clients={clients}
          onClose={() => setBillOpen(false)}
          onBilled={() => { setBillOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function JobCard({ job, drivers, onDrop, onAssign, onUnassign, onEdit, onDelete, onStatus }) {
  const st = STATUS_META[job.status] || STATUS_META.pending;
  const unassigned = drivers.filter((d) => !(job.driver_ids || []).includes(d.id));
  const [addOpen, setAddOpen] = useState(false);
  const [docketsOpen, setDocketsOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dockets = job.dockets || [];
  const assigned = (job.driver_ids || []).map(id => (job.drivers || []).find(d => d.id === id) || drivers.find(d => d.id === id) || { id, name: "Assigned driver" });
  const quiet = job.status === "done" || job.status === "cancelled";
  const hasTargets = job.target_loads != null || job.target_tonnes != null || job.target_cubic_m != null;
  return (
    <article onDragOver={e => { if (Array.from(e.dataTransfer.types).includes("text/driver-id")) { e.preventDefault(); setDragOver(true); } }} onDragLeave={() => setDragOver(false)} onDrop={e => { setDragOver(false); onDrop(e); }}
      className={`hard-card rounded-md p-3 sm:p-4 ${quiet ? "bg-zinc-50 dark:bg-white/5" : ""} ${job.priority === "urgent" ? "border-l-4 border-l-red-600" : ""} ${dragOver ? "ring-2 ring-[#FF5F15]" : ""}`}
      id={`trucking-card-${job.id}`} data-testid={`trucking-card-${job.id}`}>
      {job.master_job && <Link to={`/app/trucking/jobs/${job.master_job.id}`} className="block text-base font-bold hover:underline break-words mb-2">{job.master_job.job_number} · {job.master_job.title}</Link>}
      {!job.master_job && <p className="text-base font-bold break-words mb-2">Run {job.job_number || job.id}{job.title ? ` · ${job.title}` : ""}</p>}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-sm ${st.cls}`} data-testid={`trucking-status-${job.id}`}>{st.label}</span>
        <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{TYPE_LABELS[job.job_type] || job.job_type}</span>
        {job.priority === "urgent" && <span className="text-[10px] font-bold uppercase text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Urgent</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3 md:gap-5">
        <div className="min-w-0 md:col-span-2">
          <h3 className={`font-display font-black uppercase text-xl leading-tight break-words ${quiet ? "text-zinc-500" : ""}`}>{job.material || TYPE_LABELS[job.job_type] || "Trucking job"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-1 sm:gap-3 items-center mt-2">
            <div className="min-w-0"><p className="text-[9px] font-bold text-zinc-500 uppercase">From</p><p className="font-semibold text-sm break-words line-clamp-2" title={job.pickup_location}>{job.pickup_location || "Not recorded"}</p></div>
            <ArrowRight className="w-4 h-4 text-[#FF5F15] rotate-90 sm:rotate-0 my-1" />
            <div className="min-w-0"><p className="text-[9px] font-bold text-zinc-500 uppercase">To</p><p className="font-semibold text-sm break-words line-clamp-2" title={job.dropoff_location}>{job.dropoff_location || "Not recorded"}</p></div>
          </div>
          {(job.linked_job || job.client_name) && <div className="mt-2 text-xs">
            {job.linked_job && <Link to={`/app/jobs/${encodeURIComponent(job.linked_job.id)}`} className="inline-block py-1 hover:underline text-[#FF5F15]"><span className="font-bold">Job {job.linked_job.job_number}</span><span className="block text-zinc-700 dark:text-zinc-300">{job.linked_job.title}</span></Link>}
            {job.client_name && <p className="text-zinc-500">{job.client_name}</p>}
          </div>}
        </div>
        <div className="border-t border-zinc-200 dark:border-white/10 pt-3 min-w-0">
          <p className="text-[9px] font-bold uppercase text-zinc-500">Truck</p>
          <p className={`text-sm font-bold mb-2 ${!job.truck_id ? "text-amber-700 dark:text-amber-400" : ""}`}><Truck className="w-4 h-4 inline mr-1.5" />{job.truck_id ? job.truck?.rego || "Assigned vehicle" : "Unassigned"}</p>
          <p className="text-[9px] font-bold uppercase text-zinc-500 mb-1">Driver</p>
      {/* Drivers on this card */}
      <div className="flex items-center gap-2 flex-wrap" data-testid={`trucking-card-drivers-${job.id}`}>
        {assigned.map((d) => (
          <span key={d.id} className="inline-flex items-center gap-1.5 bg-[#09090B] text-white text-xs font-bold px-2.5 py-1">
            {d.name}
            <button onClick={() => onUnassign(d.id)} className="text-zinc-400 hover:text-[#FF5F15] p-2" data-testid={`trucking-unassign-${job.id}-${d.id}`} aria-label={`Remove ${d.name}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {(job.driver_ids || []).length === 0 && (
          <span className="text-xs text-amber-700 dark:text-amber-400 border border-dashed border-amber-400 px-3 py-1">Unassigned — drop driver here</span>
        )}
        {unassigned.length > 0 && (
          <div className="relative">
            <button onClick={() => setAddOpen((v) => !v)} className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15] border-2 border-zinc-300 hover:border-[#FF5F15] px-2 min-h-10 transition-colors" data-testid={`trucking-add-driver-${job.id}`}>
              + Driver
            </button>
            {addOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAddOpen(false)} aria-hidden="true" />
                <div className="absolute z-20 mt-1 bg-white dark:bg-[#151C27] border-2 border-zinc-900 shadow-lg min-w-[180px] max-h-48 overflow-y-auto">
                  {unassigned.map((d) => (
                    <button key={d.id} onClick={() => { onAssign(d.id); setAddOpen(false); }} className="block w-full text-left px-3 py-3 text-sm hover:bg-orange-50" data-testid={`trucking-pick-driver-${job.id}-${d.id}`}>
                      {d.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

        </div>
        <div className="border-t border-zinc-200 dark:border-white/10 pt-3 space-y-2">
          <div><p className="text-[9px] font-bold uppercase text-zinc-500">Recorded actuals</p><p className="font-bold text-base">{(job.actual_loads ?? job.loads_done) || 0} loads · {(job.actual_tonnes ?? job.tonnes_done) || 0} t{job.actual_cubic_m != null && ` · ${job.actual_cubic_m} m³`}</p></div>
          {job.target_loads != null && <RunProgress label="Target loads" actual={(job.actual_loads ?? job.loads_done)} target={job.target_loads} quiet={quiet} />}
          {job.target_tonnes != null && <RunProgress label="Target tonnes" actual={(job.actual_tonnes ?? job.tonnes_done)} target={job.target_tonnes} unit=" t" quiet={quiet} />}
          {job.target_cubic_m != null && <RunProgress label="Target volume" actual={job.actual_cubic_m || 0} target={job.target_cubic_m} unit=" m³" quiet={quiet} />}
          {!hasTargets && <p className="text-xs text-zinc-500">No target set</p>}
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-zinc-200 dark:border-white/10 flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          {(job.pickup_location || job.dropoff_location || job.notes || dockets.length > 0 || job.hours_logged > 0 || job.invoice_id) && <details>
            <summary className="text-xs text-zinc-500 cursor-pointer py-2">Run details{dockets.length > 0 ? ` · ${dockets.length} dockets` : ""}</summary>
            <div className="text-xs text-zinc-500 space-y-2 py-2">
              {job.pickup_location && <p className="break-words"><strong>From:</strong> {job.pickup_location}</p>}
              {job.dropoff_location && <p className="break-words"><strong>To:</strong> {job.dropoff_location}</p>}
              {job.notes && <p className="whitespace-pre-wrap break-words">{job.notes}</p>}
              {job.hours_logged > 0 && <p><Clock className="w-3 h-3 inline mr-1" />{job.hours_logged}h logged</p>}
              {dockets.length > 0 && <button type="button" onClick={() => setDocketsOpen(true)} className="inline-flex items-center gap-1 underline py-2" data-testid={`trucking-dockets-btn-${job.id}`}><Camera className="w-4 h-4" /> View {dockets.length} docket{dockets.length === 1 ? "" : "s"}</button>}
              {job.invoice_id && <p className="text-emerald-700 dark:text-emerald-400 font-bold" data-testid={`trucking-invoiced-badge-${job.id}`}>Invoiced{job.invoice_number ? ` ${job.invoice_number}` : ""}</p>}
            </div>
          </details>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {job.status !== "done" && <Button variant="outline" size="sm" className="h-10 rounded-md text-xs font-bold uppercase" onClick={() => onStatus(job.status === "pending" ? "in_progress" : "done")} data-testid={`trucking-advance-${job.id}`}>{job.status === "pending" ? "Start" : "Mark done"}</Button>}
          {!quiet && <Button variant="ghost" size="sm" className="h-10 text-xs" onClick={() => { if (window.confirm("Cancel this run? Recorded loads will be retained.")) onStatus("cancelled"); }}>Cancel run</Button>}
          <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`trucking-edit-${job.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="More run actions"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onSelect={onDelete} className="text-red-600" data-testid={`trucking-delete-${job.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete run</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
        </div>
      </div>
      {docketsOpen && <PhotoLightbox paths={dockets.map(d => d.path)} startIndex={0} open onClose={() => setDocketsOpen(false)} />}
    </article>
  );
}

function RunProgress({ label, actual, target, unit = "", quiet }) {
  const value = Number(actual) || 0;
  const limit = Number(target);
  const percent = limit > 0 ? Math.max(0, Math.min(100, value / limit * 100)) : 0;
  return <div>
    <p className="text-[9px] font-bold uppercase text-zinc-500">{label}</p>
    <p className="font-display font-black text-xl leading-tight">{value}<span className="text-sm font-normal text-zinc-500"> / {limit}{unit}</span></p>
    {limit > 0 && <div role="progressbar" aria-label={`${label}: ${value} of ${limit}`} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(limit, Math.max(0, value))} className="h-1.5 bg-zinc-200 dark:bg-white/10 rounded-full mt-1 overflow-hidden"><div className={`h-full ${quiet ? "bg-zinc-400" : "bg-[#FF5F15]"}`} style={{ width: `${percent}%` }} /></div>}
  </div>;
}
